import { useNotifications } from '@client/foundation/notifications/notifications.hook';
import { useCallback } from 'react';
import { request } from '@client/foundation/data-access/request';
import { buildHistoricalDownloadFileName } from '@shared/download-file-name';

// Type definition for File System Access API
interface SaveFilePickerOptions {
  suggestedName?: string;
  types?: Array<{
    description: string;
    accept: Record<string, string[]>;
  }>;
}

export const useFileDownload = () => {
  const { notify } = useNotifications();

  const downloadFile = useCallback(
    async (
      exchangeId: string,
      symbol: string,
      timeframe: string,
      historicalDays: number,
      fromDate?: string,
      toDate?: string
    ) => {
      const defaultFileName = buildHistoricalDownloadFileName({
        symbol,
        timeframe,
        fromDate,
        toDate
      });

      try {
        // Check if File System Access API is supported
        if (
          'showSaveFilePicker' in window &&
          typeof window.showSaveFilePicker === 'function'
        ) {
          // Modern approach: File System Access API
          const showSaveFilePicker = window.showSaveFilePicker as (
            options: SaveFilePickerOptions
          ) => Promise<FileSystemFileHandle>;

          const handle = await showSaveFilePicker({
            suggestedName: defaultFileName,
            types: [
              {
                description: 'CSV Files',
                accept: {
                  'text/csv': ['.csv']
                }
              }
            ]
          });

          // Now fetch the data
          const response = await request(
            `/rest/download?exchangeId=${exchangeId}&symbol=${symbol}&timeframe=${timeframe}&historicalDays=${historicalDays}&fromDate=${fromDate}&toDate=${toDate}`
          );

          if (response.ok) {
            const blob = await response.blob();

            // Use the handle we got earlier
            const writable = await handle.createWritable();
            await writable.write(blob);
            await writable.close();

            notify({
              message: 'File downloaded successfully!',
              type: 'success'
            });
          } else {
            throw new Error('Failed to fetch CSV data');
          }
        } else {
          // Fallback for browsers without File System Access API
          const response = await request(
            `/rest/download?exchangeId=${exchangeId}&symbol=${symbol}&timeframe=${timeframe}&historicalDays=${historicalDays}&fromDate=${fromDate}&toDate=${toDate}`
          );

          if (response.ok) {
            const blob = await response.blob();

            // Traditional blob download method
            const link = document.createElement('a');
            const url = URL.createObjectURL(blob);
            link.href = url;
            link.download = defaultFileName;

            // Append to body, click, and remove
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);

            // Clean up the URL object
            setTimeout(() => URL.revokeObjectURL(url), 100);

            notify({
              message: 'File downloaded successfully!',
              type: 'success'
            });
          } else {
            throw new Error('Failed to fetch CSV data');
          }
        }
      } catch (error) {
        console.error('Download failed:', error);
        notify({
          message: 'Failed to download file. Please try again.',
          type: 'error'
        });
      }
    },
    [notify]
  );

  return { downloadFile };
};
